import React, { useEffect, useRef } from 'react';

interface ChatWidgetConfig {
  provider: 'intercom' | 'drift' | 'crisp' | 'tawk' | 'custom';
  config: Record<string, any>;
}

interface BodyEndData {
  scriptLinks?: Array<{
    src: string;
    async?: boolean;
    defer?: boolean;
    id?: string;
    crossorigin?: string;
    integrity?: string;
  }>;
  inlineScripts?: string;
  chatWidgets?: ChatWidgetConfig[];
  cookieConsent?: {
    enabled: boolean;
    config?: {
      theme?: string;
      position?: string;
      message?: string;
      buttonText?: string;
      privacyPolicyUrl?: string;
    };
  };
  analytics?: {
    trackingEvents?: Array<{
      eventName: string;
      eventData?: Record<string, any>;
    }>;
  };
  performanceMonitoring?: {
    enabled: boolean;
    provider?: string;
    config?: Record<string, any>;
  };
  noScriptContent?: string;
}

interface BodyEndComponentProps {
  component: {
    id: string;
    type: 'body-end';
    data: Partial<BodyEndData>;
    order: number;
    visible: boolean;
    createdAt?: string;
    updatedAt?: string;
  };
}

export const BodyEndComponent: React.FC<BodyEndComponentProps> = ({
  component,
}) => {
  const { data } = component;
  const scriptRefs = useRef<HTMLScriptElement[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load external scripts
  useEffect(() => {
    if (!data.scriptLinks || typeof window === 'undefined') return;

    const scripts: HTMLScriptElement[] = [];

    data.scriptLinks.forEach((scriptInfo) => {
      const script = document.createElement('script');
      script.src = scriptInfo.src;
      if (scriptInfo.async) script.async = true;
      if (scriptInfo.defer) script.defer = true;
      if (scriptInfo.id) script.id = scriptInfo.id;
      if (scriptInfo.crossorigin) script.crossOrigin = scriptInfo.crossorigin;
      if (scriptInfo.integrity) script.integrity = scriptInfo.integrity;

      document.body.appendChild(script);
      scripts.push(script);
    });

    scriptRefs.current = scripts;

    return () => {
      scripts.forEach((script) => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      });
    };
  }, [data.scriptLinks]);

  // Load inline scripts
  useEffect(() => {
    if (!data.inlineScripts || typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.innerHTML = data.inlineScripts;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [data.inlineScripts]);

  // Initialize chat widgets
  useEffect(() => {
    if (!data.chatWidgets || typeof window === 'undefined') return;

    data.chatWidgets.forEach((widget) => {
      switch (widget.provider) {
        case 'intercom':
          if (widget.config.app_id) {
            const intercomScript = document.createElement('script');
            intercomScript.innerHTML = `
              window.intercomSettings = ${JSON.stringify(widget.config)};
              (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${widget.config.app_id}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
            `;
            document.body.appendChild(intercomScript);
          }
          break;

        case 'drift':
          if (widget.config.embed_id) {
            const driftScript = document.createElement('script');
            driftScript.innerHTML = `
              !function() {
                var t = window.driftt = window.drift = window.driftt || [];
                if (!t.init) {
                  if (t.invoked) return void (window.console && console.error && console.error("Drift snippet included twice."));
                  t.invoked = !0, t.methods = [ "identify", "config", "track", "reset", "debug", "show", "ping", "page", "hide", "off", "on" ],
                  t.factory = function(e) {
                    return function() {
                      var n = Array.prototype.slice.call(arguments);
                      return n.unshift(e), t.push(n), t;
                    };
                  }, t.methods.forEach(function(e) {
                    t[e] = t.factory(e);
                  }), t.load = function(t) {
                    var e = 3e5, n = Math.ceil(new Date() / e) * e, o = document.createElement("script");
                    o.type = "text/javascript", o.async = !0, o.crossorigin = "anonymous", o.src = "https://js.driftt.com/include/" + n + "/" + t + ".js";
                    var i = document.getElementsByTagName("script")[0];
                    i.parentNode.insertBefore(o, i);
                  };
                }
              }();
              drift.SNIPPET_VERSION = '0.3.1';
              drift.load('${widget.config.embed_id}');
            `;
            document.body.appendChild(driftScript);
          }
          break;

        case 'crisp':
          if (widget.config.website_id) {
            const crispScript = document.createElement('script');
            crispScript.innerHTML = `
              window.$crisp=[];window.CRISP_WEBSITE_ID="${widget.config.website_id}";
              (function(){d=document;s=d.createElement("script");s.src="https://client.crisp.chat/l.js";s.async=1;d.getElementsByTagName("head")[0].appendChild(s);})();
            `;
            document.body.appendChild(crispScript);
          }
          break;

        case 'tawk':
          if (widget.config.property_id && widget.config.widget_id) {
            const tawkScript = document.createElement('script');
            tawkScript.innerHTML = `
              var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
              (function(){
                var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
                s1.async=true;
                s1.src='https://embed.tawk.to/${widget.config.property_id}/${widget.config.widget_id}';
                s1.charset='UTF-8';
                s1.setAttribute('crossorigin','*');
                s0.parentNode.insertBefore(s1,s0);
              })();
            `;
            document.body.appendChild(tawkScript);
          }
          break;

        case 'custom':
          if (widget.config.script) {
            const customScript = document.createElement('script');
            customScript.innerHTML = widget.config.script;
            document.body.appendChild(customScript);
          }
          break;
      }
    });
  }, [data.chatWidgets]);

  // Cookie consent
  useEffect(() => {
    if (!data.cookieConsent?.enabled || typeof window === 'undefined') return;

    const config = data.cookieConsent.config || {};
    const cookieScript = document.createElement('script');
    cookieScript.innerHTML = `
      // Simple cookie consent implementation
      if (!localStorage.getItem('cookieConsent')) {
        const banner = document.createElement('div');
        banner.style.cssText = 'position: fixed; ${config.position === 'top' ? 'top: 0' : 'bottom: 0'}; left: 0; right: 0; background: ${config.theme === 'dark' ? '#333' : '#fff'}; color: ${config.theme === 'dark' ? '#fff' : '#333'}; padding: 20px; text-align: center; z-index: 9999; box-shadow: 0 -2px 10px rgba(0,0,0,0.1);';
        banner.innerHTML = '<p style="margin: 0 0 10px 0;">${config.message || 'This website uses cookies to ensure you get the best experience.'}</p><button style="background: #007bff; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">${config.buttonText || 'Accept'}</button>${config.privacyPolicyUrl ? `<a href="${config.privacyPolicyUrl}" style="margin-left: 10px; color: #007bff;">Privacy Policy</a>` : ''}';
        document.body.appendChild(banner);

        banner.querySelector('button').addEventListener('click', function() {
          localStorage.setItem('cookieConsent', 'true');
          banner.remove();
        });
      }
    `;
    document.body.appendChild(cookieScript);
  }, [data.cookieConsent]);

  // Analytics tracking events
  useEffect(() => {
    if (!data.analytics?.trackingEvents || typeof window === 'undefined') return;

    data.analytics.trackingEvents.forEach((event) => {
      // Google Analytics
      if ((window as any).gtag) {
        (window as any).gtag('event', event.eventName, event.eventData);
      }
      // Facebook Pixel
      if ((window as any).fbq) {
        (window as any).fbq('track', event.eventName, event.eventData);
      }
      // Custom event
      window.dispatchEvent(new CustomEvent(event.eventName, { detail: event.eventData }));
    });
  }, [data.analytics]);

  // NoScript fallback
  const renderNoScript = () => {
    if (!data.noScriptContent) return null;

    return (
      <noscript dangerouslySetInnerHTML={{ __html: data.noScriptContent }} />
    );
  };

  // This component doesn't render visible content
  // It only injects scripts and manages external resources
  return (
    <>
      <div ref={containerRef} style={{ display: 'none' }} data-component="body-end" />
      {renderNoScript()}
    </>
  );
};